<template>
  <AnalyticsWidget
    title="Orders IFOT Breakdown"
    subTitle="Orders completed in-full on-time scoring during the date ranges"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">

    <LoadingPanel :loading="loading" />
    <AnalyticsBreakdownTable v-if="!loading">
      <template v-slot:header>
        <tr>
          <th></th>
          <th>Completed</th>
          <th v-for="taskType of taskTypeList" :key="taskType.key">
            <Tooltip :label="`Percentage of orders for ${taskType.name} despatched on the same day as estimated/requested`" helpStyle>{{taskType.name}} Score</Tooltip>
          </th>
        </tr>
      </template>
      <template v-slot:body>
        <tr class="highlight">
          <td>
            Today
            <InlineHelpPopover title="Today" placement="right" inline>
              <p>Today&apos;s orders compared to previous working day&apos;s orders on {{formatDate(orderBreakdownData.day.previous.startDate)}}</p>              
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="calculateTotalOrderCount(orderBreakdownData.day.current.completedData)" :previousValue="calculateTotalOrderCount(orderBreakdownData.day.previous.completedData)" highlight>{{calculateTotalOrderCount(orderBreakdownData.day.current.completedData)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell
            v-for="taskType of taskTypeList" :key="taskType.key"
            :value="calculateCurrentIFOTScore(orderBreakdownData.day.current, taskType.id)"
            :previousValue="calculatePreviousIFOTScore(orderBreakdownData.day.previous, taskType.id)">
            {{formatPercent(calculateCurrentIFOTScore(orderBreakdownData.day.current, taskType.id))}}
          </AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This Week
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This week&apos;s orders compared to same day of the previous week&apos;s orders from {{formatDate(orderBreakdownData.week.previous.startDate)}} to {{formatDate(orderBreakdownData.week.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>

          <AnalyticsBreakdownValueCell :value="calculateTotalOrderCount(orderBreakdownData.week.current.completedData)" :previousValue="calculateTotalOrderCount(orderBreakdownData.week.previous.completedData)">{{calculateTotalOrderCount(orderBreakdownData.week.current.completedData)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell
            v-for="taskType of taskTypeList" :key="taskType.key"
            :value="calculateCurrentIFOTScore(orderBreakdownData.week.current, taskType.id)"
            :previousValue="calculatePreviousIFOTScore(orderBreakdownData.week.previous, taskType.id)">
            {{formatPercent(calculateCurrentIFOTScore(orderBreakdownData.week.current, taskType.id))}}
          </AnalyticsBreakdownValueCell>
          
        </tr>
        <tr>
          <td>
            This Month
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This month&apos;s orders compared to same day of the previous month&apos;s orders from {{formatDate(orderBreakdownData.month.previous.startDate)}} to {{formatDate(orderBreakdownData.month.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>

          <AnalyticsBreakdownValueCell :value="calculateTotalOrderCount(orderBreakdownData.month.current.completedData)" :previousValue="calculateTotalOrderCount(orderBreakdownData.month.previous.completedData)">{{calculateTotalOrderCount(orderBreakdownData.month.current.completedData)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell
            v-for="taskType of taskTypeList" :key="taskType.key"
            :value="calculateCurrentIFOTScore(orderBreakdownData.month.current, taskType.id)"
            :previousValue="calculatePreviousIFOTScore(orderBreakdownData.month.previous, taskType.id)">
            {{formatPercent(calculateCurrentIFOTScore(orderBreakdownData.month.current, taskType.id))}}
          </AnalyticsBreakdownValueCell>
          
        </tr>
        <tr>
          <td>
            This FY
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This financial year&apos;s orders compared to same day of the previous financial year&apos;s orders from {{formatDate(orderBreakdownData.financialYear.previous.startDate)}} to {{formatDate(orderBreakdownData.financialYear.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>

          <AnalyticsBreakdownValueCell :value="calculateTotalOrderCount(orderBreakdownData.financialYear.current.completedData)" :previousValue="calculateTotalOrderCount(orderBreakdownData.financialYear.previous.completedData)">{{calculateTotalOrderCount(orderBreakdownData.financialYear.current.completedData)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell
            v-for="taskType of taskTypeList" :key="taskType.key"
            :value="calculateCurrentIFOTScore(orderBreakdownData.financialYear.current, taskType.id)"
            :previousValue="calculatePreviousIFOTScore(orderBreakdownData.financialYear.previous, taskType.id)">
            {{formatPercent(calculateCurrentIFOTScore(orderBreakdownData.financialYear.current, taskType.id))}}
          </AnalyticsBreakdownValueCell>
          
        </tr>
      </template>

    </AnalyticsBreakdownTable>
  </AnalyticsWidget>
</template>


<script>
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsBreakdownTable from '@/modules/intelligence/components/analyticsBreakdownTable'
import AnalyticsBreakdownValueCell from '@/modules/intelligence/components/analyticsBreakdownValueCell'

export default {
  name: 'OrderDespatchBreakdownWidget',
  components: {
    AnalyticsWidget,
    AnalyticsBreakdownTable,
    AnalyticsBreakdownValueCell
  },
  props: {
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    },
    taskTypeList() {
      return this.orderBreakdownData.taskTypeList || []
    },
  },
  data() {
    return {
      loading: true,
      orderBreakdownData: {}
    }
  },
  watch: {
    contactId: function() {
      this.requestData()
    }
  },
  mounted() {
    this.loading = true
    this.requestData()

    this.subscribeEndpoint('/orders', () => {
      this.requestData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  methods: {
    requestData() {
      this.$apiService.analyticsService.getOrderDespatchBreakdown({ contactId: this.contactId })
      .then(response => {
        this.orderBreakdownData = response.data
      })
      .finally(() => {
        this.loading = false
      })
      .catch(err => {
      })
    },
    calculateTotalOrderCount(completedList) {
      return completedList.reduce((total, item) => total + item.totalOrderCount, 0)
    },
    calculateCurrentIFOTScore(breakdownPeriod, taskTypeId) {
      const taskTypeData = breakdownPeriod.completedData.filter(item => item.despatchTaskTypeId === taskTypeId)
      const dueData = breakdownPeriod.dueData.filter(item => item.despatchTaskTypeId === taskTypeId)

      var dueOrderCount = dueData.reduce((total, item) => total + item.totalOrderCount, 0)
      var totalOrderCompletedCount = taskTypeData.reduce((total, item) => total + item.totalOrderCount, 0) + dueOrderCount
      var totalCompletedLateCount = taskTypeData.reduce((total, item) => total + item.completedLateCount, 0) + dueOrderCount    // Completed Late Also includes overdue orders

      if (totalOrderCompletedCount === 0) {
        return 0
      }
      return ((totalOrderCompletedCount - totalCompletedLateCount) / totalOrderCompletedCount * 100)
    },
    calculatePreviousIFOTScore(breakdownPeriod, taskTypeId) {
      const taskTypeData = breakdownPeriod.completedData.filter(item => item.despatchTaskTypeId === taskTypeId)

      // Note: No due data for previous periods as we are only looking at completed orders
      var totalOrderCompletedCount = taskTypeData.reduce((total, item) => total + item.totalOrderCount, 0)
      var totalCompletedLateCount = taskTypeData.reduce((total, item) => total + item.completedLateCount, 0)

      if (totalOrderCompletedCount === 0) {
        return 0
      }
      return ((totalOrderCompletedCount - totalCompletedLateCount) / totalOrderCompletedCount * 100)
    },
  }
}
</script>